import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Code, Mail, Package, Server, Share2, X } from 'react-feather';
import { ToolbarButton } from '../../components/ConfigPages';
import {
  StepRail, WizardFrame, EtlBanner, EtlSection, EtlGrid, EtlField, EtlReadonly,
  ChoiceCards, FilterSelect, EtlLoading,
} from '../../components/EtlPages';
import EmailIntegrationConfig from '../../components/SchemaBuilder/EmailIntegrationConfig';
import FileUploadComponent from '../../components/SchemaBuilder/FileUploadComponent';
import DataPreviewTabs from '../../components/SchemaBuilder/DataPreviewTabs';
import AiExtractionTab from '../../components/SchemaBuilder/AiExtractionTab';
import FieldMappingPanel from '../../components/SchemaBuilder/FieldMappingPanel';
import HistoricalRevenueMapping from '../../components/SchemaBuilder/HistoricalRevenueMapping';
import SchemaPreview from '../../components/SchemaBuilder/SchemaPreview';
import SchemaJsonEditor from '../../components/SchemaBuilder/SchemaJsonEditor';
import ToastSftpConfig from '../../components/SchemaBuilder/ToastSftpConfig';
import MewsConfig from '../../components/SchemaBuilder/MewsConfig';
import M3Config from '../../components/SchemaBuilder/M3Config';
import OhipConfig from '../../components/SchemaBuilder/OhipConfig';
import ZipBundleConfig from '../../components/SchemaBuilder/ZipBundleConfig';
import { processFormRecognizerFile, saveSchemaConfig, getSchemaConfig } from '../../utils/api';

/**
 * Source kinds.
 *
 * The old setup screen asked two questions to answer one: "Import File Type"
 * (Hotel / Restaurant / PMS) and then, conditionally, a second dropdown for the
 * restaurant or PMS integration. Hotel and Restaurant→Toast Email both fell
 * through to the identical file flow, so the first question only ever existed
 * to reveal the second. One choice, four answers.
 */
const SOURCE_KINDS = [
  { value: 'file', title: 'File feed', icon: Mail, sub: 'A report that arrives by email or lands on SFTP — night audit, revenue, statistics, market segments.' },
  { value: 'toast_sftp_integration', title: 'Toast SFTP', icon: Share2, sub: 'Restaurant sales pulled from a Toast SFTP export.' },
  { value: 'mews', title: 'Mews PMS', icon: Server, sub: 'Direct API connection to a Mews property.' },
  { value: 'ohip', title: 'Oracle OHIP', icon: Server, sub: 'Direct API connection through the Oracle Hospitality Integration Platform.' },
  { value: 'm3', title: 'M3 Accounting', icon: Server, sub: 'Direct API connection to M3 Accounting Core.' },
];

const INTEGRATION_KINDS = ['toast_sftp_integration', 'mews', 'ohip', 'm3'];

// Steps whose content is derived from the sample file, and so must be reset
// when a different one is uploaded (see `dataEpoch`).
const DOWNSTREAM = ['preview', 'extract', 'historical', 'map', 'save'];

// File types probed when we look for a schema that already exists at this location.
const COMMON_FILE_TYPES = ['audit', 'revenue', 'stat', 'dsr', 'market', 'historical'];

const emptySchemaConfig = (hotelId) => ({
  hotelId: hotelId || '',
  fileType: '',
  ocrService: 'formRecognizer',
  schemaVersion: '1.0',
  fieldMappings: {},
  transformations: {},
  validationRules: {},
  selectedPages: null,
  preprocess: false,
});

/**
 * SchemaWizard — define how one feed is recognised, read and mapped.
 *
 * What changed from the old Schema Builder page, and why:
 *
 *  · STEPS ARE KEYED, NUMBERED BY POSITION. The old rail rendered the raw step
 *    id, so the branch where AI extraction returns database-ready rows (ids
 *    1,2,3,4,6) drew a step labelled "6" in the fifth slot with a dead gap
 *    before it. Skipped steps now leave the list entirely.
 *
 *  · THE RAIL NAVIGATES. Completed steps are buttons. Getting from Save back
 *    to Upload used to be four presses of Back.
 *
 *  · ONE FOOTER. Each step component used to draw its own Back/Continue pair —
 *    six steps, six different footers, one of them missing Back. Steps now
 *    publish their primary action upward and the frame renders it.
 *
 *  · SETUP IS STEP ONE, NOT A DIFFERENT PAGE. The location picker was a
 *    separate full-screen render with its own header and its own loading
 *    spinner; leaving it was a one-way door.
 */
export default function SchemaWizard({ hotelId: hotelIdParam, scope, onExit }) {
  const [searchParams] = useSearchParams();
  const presetFileType = searchParams.get('fileType');

  const { locations, locationOptions, locationsLoading } = scope;

  const [locationId, setLocationId] = useState(hotelIdParam ? String(hotelIdParam) : null);
  const [sourceKind, setSourceKind] = useState('file');
  const [stepKey, setStepKey] = useState(hotelIdParam ? 'match' : 'source');

  const [emailConfig, setEmailConfig] = useState({ emailMatchType: 'file', auditFile: '', fileType: presetFileType || 'audit' });
  const [schemaConfig, setSchemaConfig] = useState(() => emptySchemaConfig(hotelIdParam));
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [aiExtractedData, setAiExtractedData] = useState(null);
  // Confirmation is read back in the SAME event that sets it (the Extraction
  // step confirms and advances in one click), so the state value is a render
  // behind. A ref carries the truth across that gap — see `onNext` below.
  const aiConfirmedRef = useRef(false);
  const [integrationInitialConfig, setIntegrationInitialConfig] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingSchema, setExistingSchema] = useState(null);
  const [checkingSchema, setCheckingSchema] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  // Guards the one-time seed below so it can never overwrite the user's edits.
  const seededRef = useRef(false);
  const [showZipBundles, setShowZipBundles] = useState(false);

  // Steps publish their primary action here so the frame can own the footer.
  const [stepFooter, setStepFooter] = useState(null);
  useEffect(() => { setStepFooter(null); }, [stepKey]);

  /**
   * Every step the user has reached stays MOUNTED, hidden with CSS, rather
   * than being unmounted when you move on.
   *
   * This is the fix for the oldest complaint about this flow: going Back threw
   * away what you had typed. All the wizard-level state (email config, schema
   * config, extraction results) lived up here and survived, but each step
   * component also holds its own — the chosen sample file and page selection
   * in Upload, the draft column mappings in Field Mapping, the extraction
   * result in Extraction — and unmounting took that with it. Keeping visited
   * steps alive preserves all of it with no change to those components.
   *
   * Only steps actually reached are mounted, so the heavy panels never load
   * for a flow that skips them.
   */
  const [visited, setVisited] = useState(() => new Set([hotelIdParam ? 'match' : 'source']));

  /**
   * Counter for "the source data underneath changed".
   *
   * Keeping steps mounted preserves work, but it must not preserve work that
   * is now WRONG: uploading a different sample file, or switching to a
   * different file type, invalidates everything downstream of it. Bumping this
   * re-keys the downstream steps so they remount clean, while plain Back /
   * forward navigation leaves it alone and keeps every input intact.
   */
  const [dataEpoch, setDataEpoch] = useState(0);
  useEffect(() => {
    setVisited((prev) => (prev.has(stepKey) ? prev : new Set(prev).add(stepKey)));
  }, [stepKey]);


  const selectedLocation = useMemo(() => {
    if (!locationId) return null;
    const match = (locations || []).find((l) => String(l.locationId || l.id) === String(locationId));
    if (!match) return null;
    return { ...match, id: match.locationId || match.id, orgId: match.orgId || match.organizationId };
  }, [locations, locationId]);

  /**
   * A readable organization label. `scope.selectedOrgCode` can be the
   * ALL_ORGANIZATIONS sentinel, which must never reach the screen; once a
   * location is chosen its own organization is the accurate answer anyway.
   */
  const orgLabel = useMemo(() => {
    if (selectedLocation) {
      const own = scope.availableOrgs.find((o) => o.code === selectedLocation.organizationCode || o.id === selectedLocation.orgId);
      if (own) return own.name || own.code;
      if (selectedLocation.organizationName) return selectedLocation.organizationName;
    }
    const match = scope.availableOrgs.find((o) => o.code === scope.selectedOrgCode);
    if (match) return match.name || match.code;
    return scope.availableOrgs.length > 1 ? 'All organizations' : '—';
  }, [selectedLocation, scope.availableOrgs, scope.selectedOrgCode]);

  const isHistorical = emailConfig.fileType === 'historical';
  const isIntegration = INTEGRATION_KINDS.includes(sourceKind);
  const aiConfirmed = !!aiExtractedData?.confirmed;
  /**
   * Which read path the sample file took. Only AI extraction gets the
   * "Extraction" step — Form Recognizer / Textract / parsed CSV+Excel hand
   * back raw tables and go straight from Preview to Field mapping. The step
   * list used to insert Extraction unconditionally, so every OCR and CSV feed
   * landed on the AI page after Preview with no route to mapping.
   * Until a file is uploaded the rail previews the AI path (the upload
   * screen's recommended default).
   */
  const readMethod = schemaConfig.extractionMethod || schemaConfig.ocrService;
  const isAiPath = uploadedFile ? readMethod === 'aiExtraction' : true;

  /**
   * The step list, derived rather than declared. `reached` marks how far the
   * user has actually got, which is what makes the rail navigable without
   * letting someone jump to Save before there is anything to save.
   */
  const steps = useMemo(() => {
    if (isIntegration) {
      const kind = SOURCE_KINDS.find((k) => k.value === sourceKind);
      return [
        { key: 'source', title: 'Source', sub: 'Location and connection type' },
        { key: 'connect', title: 'Connection', sub: kind ? kind.title : 'Credentials and options' },
      ];
    }
    const list = [
      { key: 'source', title: 'Source', sub: 'Location and connection type' },
      { key: 'match', title: 'Recognise', sub: 'How we spot this file' },
      { key: 'upload', title: 'Sample file', sub: 'Upload one to work from' },
    ];
    // Preview only exists on the OCR path — AI extraction reads the file itself.
    if (extractedData) list.push({ key: 'preview', title: 'Preview', sub: 'Check what came out' });
    if (isHistorical && !isAiPath) {
      list.push({ key: 'historical', title: 'Historical mapping', sub: 'Columns → revenue & room stats' });
    } else if (isAiPath) {
      list.push({ key: 'extract', title: 'Extraction', sub: 'Let the model read it' });
      // AI extraction that produced database-ready rows makes manual field
      // mapping redundant — drop the step instead of numbering around it.
      if (!aiConfirmed) list.push({ key: 'map', title: 'Field mapping', sub: 'Columns → destination tables' });
    } else {
      // OCR / parsed spreadsheet: the model never reads this file, so there is
      // no Extraction step — the preview tables are what gets mapped.
      list.push({ key: 'map', title: 'Field mapping', sub: 'Columns → destination tables' });
    }
    list.push({ key: 'save', title: 'Review & save', sub: 'Confirm and write the config' });
    return list;
  }, [isIntegration, sourceKind, extractedData, isHistorical, aiConfirmed, isAiPath]);

  // Highest step the user has legitimately reached, so the rail can offer it.
  const [maxReachedKey, setMaxReachedKey] = useState('source');
  useEffect(() => {
    const order = steps.map((s) => s.key);
    if (order.indexOf(stepKey) > order.indexOf(maxReachedKey)) setMaxReachedKey(stepKey);
  }, [stepKey, steps, maxReachedKey]);

  /**
   * A step can be removed from the list while you are standing on it — the
   * Extraction step drops "Field mapping" the moment its output is confirmed.
   * Nothing renders a step that is no longer in `steps`, so without this the
   * wizard went blank: rail with no active step, empty pane, no way forward.
   * Land on whatever now occupies that position instead.
   */
  const prevStepsRef = useRef(steps);
  useEffect(() => {
    const order = steps.map((s) => s.key);
    if (!order.includes(stepKey)) {
      const prevOrder = prevStepsRef.current.map((s) => s.key);
      const i = prevOrder.indexOf(stepKey);
      setStepKey(order[Math.min(i < 0 ? 0 : i, order.length - 1)] || order[0]);
    }
    prevStepsRef.current = steps;
  }, [steps, stepKey]);

  const railSteps = useMemo(() => {
    const order = steps.map((s) => s.key);
    const currentIdx = order.indexOf(stepKey);
    const maxIdx = order.indexOf(maxReachedKey);
    return steps.map((s, i) => ({
      ...s,
      complete: i < currentIdx,
      enabled: i <= Math.max(maxIdx, currentIdx),
    }));
  }, [steps, stepKey, maxReachedKey]);

  const goto = useCallback((key) => { setError(null); setStepKey(key); }, []);

  // Neighbours of a GIVEN step. Every visited step is mounted, so deriving
  // these from the active step would wire hidden steps to the wrong targets.
  const stepBefore = useCallback((key) => {
    const i = steps.findIndex((s) => s.key === key);
    return i > 0 ? steps[i - 1].key : null;
  }, [steps]);
  const stepAfter = useCallback((key) => {
    const i = steps.findIndex((s) => s.key === key);
    return i > -1 && i < steps.length - 1 ? steps[i + 1].key : null;
  }, [steps]);
  const stepNumber = useCallback((key) => steps.findIndex((s) => s.key === key) + 1, [steps]);

  // Mounted-but-hidden steps must not publish a footer over the active one.
  const footerFor = useCallback((key) => (key === stepKey ? setStepFooter : undefined), [stepKey]);

  // ── Existing-schema probe ────────────────────────────────────
  // Surfaced as an editable fact on the Source step, not as a mystery
  // "Edit Existing Schema" button that appeared once a location was picked.
  useEffect(() => {
    if (!locationId || isIntegration) { setExistingSchema(null); return; }
    let alive = true;
    (async () => {
      setCheckingSchema(true);
      setExistingSchema(null);
      const probe = presetFileType ? [presetFileType, ...COMMON_FILE_TYPES] : COMMON_FILE_TYPES;
      for (const fileType of probe) {
        try {
          const response = await getSchemaConfig(locationId, fileType);
          if (!alive) return;
          if (response?.success && response.data) { setExistingSchema(response.data); break; }
        } catch (err) {
          // No schema for this file type — keep probing.
        }
      }
      if (alive) setCheckingSchema(false);
    })();
    return () => { alive = false; };
  }, [locationId, isIntegration, presetFileType]);

  /**
   * Opening an existing feed must LOAD it, not present a blank form.
   *
   * The probe above already fetches the saved document; without this the
   * wizard showed empty keyword fields for a feed that plainly has them, and
   * the config only appeared if you happened to re-pick the same file type on
   * the Recognise step (which is what triggered the reload path). Seeds once —
   * `seededRef` makes sure a later re-probe cannot stomp on live edits.
   */
  useEffect(() => {
    if (seededRef.current || isIntegration || !existingSchema || !locationId) return;
    // Only adopt a document that matches the feed we were asked to open.
    if (presetFileType && existingSchema.fileType && existingSchema.fileType !== presetFileType) return;
    seededRef.current = true;
    setSchemaConfig((prev) => ({
      ...prev,
      ...existingSchema,
      hotelId: locationId,
      fileType: existingSchema.fileType || prev.fileType,
      ocrService: existingSchema.ocrService || prev.ocrService,
      selectedPages: existingSchema.selectedPages || null,
    }));
    if (existingSchema.emailIntegration) {
      setEmailConfig({
        ...existingSchema.emailIntegration,
        fileType: existingSchema.emailIntegration.fileType || existingSchema.fileType,
      });
    } else if (existingSchema.auditFile) {
      // Pre-emailIntegration documents keep the match string at the top level.
      setEmailConfig((prev) => ({
        ...prev,
        auditFile: existingSchema.auditFile,
        fileType: existingSchema.fileType || prev.fileType,
      }));
    }
  }, [existingSchema, isIntegration, locationId, presetFileType]);

  // Integrations recall their own Cosmos entry when one exists.
  useEffect(() => {
    if (!isIntegration || !locationId) { setIntegrationInitialConfig(null); return; }
    let alive = true;
    (async () => {
      try {
        const response = await getSchemaConfig(locationId, sourceKind);
        if (alive && response?.success && response.data) setIntegrationInitialConfig(response.data);
      } catch (err) {
        // A new integration has no entry yet — expected.
      }
    })();
    return () => { alive = false; };
  }, [isIntegration, locationId, sourceKind]);

  // ── Handlers carried over verbatim from the old page ─────────
  const handleEmailConfigChange = useCallback(async (next) => {
    const previousFileType = emailConfig.fileType;
    setEmailConfig(next);
    const actualFileType = next.fileType === 'custom' ? next.customFileType : next.fileType;
    setSchemaConfig((prev) => ({ ...prev, fileType: actualFileType }));

    // Only re-probe when the file type actually changed, or the initial mount
    // would clobber a freshly loaded schema with the 'audit' default.
    if (!locationId || !actualFileType || previousFileType === next.fileType) return;

    // A different file type is a different schema — drop anything downstream.
    setDataEpoch((n) => n + 1);
    setUploadedFile(null);
    setExtractedData(null);
    setAiExtractedData(null);
    setVisited((prev) => {
      const next2 = new Set(prev);
      for (const key of DOWNSTREAM) next2.delete(key);
      return next2;
    });
    try {
      const response = await getSchemaConfig(locationId, actualFileType);
      if (response.success && response.data) {
        setSchemaConfig((prev) => ({
          ...prev,
          ...response.data,
          hotelId: locationId,
          fileType: actualFileType,
          ocrService: response.data.ocrService || prev.ocrService,
          selectedPages: response.data.selectedPages || null,
        }));
        if (response.data.emailIntegration) setEmailConfig(response.data.emailIntegration);
        return;
      }
    } catch (err) {
      // Falls through to the reset below.
    }
    setSchemaConfig({ ...emptySchemaConfig(locationId), fileType: actualFileType, dateConfig: {} });
  }, [emailConfig.fileType, locationId]);

  const handleFileUpload = useCallback(async (file, fileType, extractionMethod = 'aiExtraction', selectedPagesOrSheets = null, preprocess = false) => {
    setIsLoading(true);
    setError(null);
    try {
      setUploadedFile(file);
      // New sample file — everything derived from the old one is stale.
      setDataEpoch((n) => n + 1);
      setAiExtractedData(null);
      aiConfirmedRef.current = false;
      setExtractedData(null);
      setVisited((prev) => {
        const next = new Set(prev);
        for (const key of DOWNSTREAM) next.delete(key);
        return next;
      });
      const method = extractionMethod;

      const configuredFileType = emailConfig.fileType === 'custom' ? emailConfig.customFileType : emailConfig.fileType;
      const finalFileType = configuredFileType || fileType;

      setSchemaConfig((prev) => ({
        ...prev,
        fileType: finalFileType,
        fileName: file.name,
        extractionMethod: method,
        aiExtraction: method === 'aiExtraction' && prev.aiExtraction ? { ...prev.aiExtraction, confirmed: false } : null,
        aiExtractionInfo: null,
        aiExtractedData: null,
        etlTableConfig: method === 'aiExtraction' ? prev.etlTableConfig : {},
        ocrService: method, // kept for backward compatibility with older configs
        selectedPages: selectedPagesOrSheets,
        preprocess,
      }));

      if (method === 'aiExtraction') {
        // AI reads the source file directly — there is nothing to preview.
        setExtractedData(null);
        goto('extract');
      } else {
        const response = await processFormRecognizerFile(file, finalFileType, method, selectedPagesOrSheets, preprocess);
        setExtractedData(response.extractedData || response);
        goto('preview');
      }
    } catch (err) {
      setError(err.message || 'Failed to process file');
    } finally {
      setIsLoading(false);
    }
  }, [emailConfig, goto]);

  // PDF manual mapping needs an explicit OCR choice on the upload step.
  const skipToMapping = useCallback(async () => {
    const name = uploadedFile?.name?.toLowerCase() || '';
    if (name.endsWith('.pdf')) {
      goto('upload');
      return;
    }
    if (!uploadedFile) return;
    const method = name.endsWith('.txt') ? 'text-parser' : 'excel-parser';
    setIsLoading(true);
    setError(null);
    try {
      const response = await processFormRecognizerFile(
        uploadedFile, schemaConfig.fileType, method, schemaConfig.selectedPages, false
      );
      setExtractedData(response.extractedData || response);
      setAiExtractedData(null);
      aiConfirmedRef.current = false;
      setSchemaConfig((prev) => ({
        ...prev, extractionMethod: method, ocrService: method,
        aiExtraction: null, aiExtractionInfo: null, aiExtractedData: null,
        etlTableConfig: {}, preprocess: false,
      }));
      goto('preview');
    } catch (err) {
      setError(err.message || 'Failed to parse the file');
    } finally {
      setIsLoading(false);
    }
  }, [uploadedFile, schemaConfig.fileType, schemaConfig.selectedPages, goto]);

  const handleMappingChange = useCallback((newMappings) => {
    setSchemaConfig((prev) => ({ ...prev, fieldMappings: { ...prev.fieldMappings, ...newMappings } }));
  }, []);

  const handleAiExtractedData = useCallback((data) => {
    aiConfirmedRef.current = !!data.confirmed;
    setAiExtractedData(data);
    setSchemaConfig((prev) => ({
      ...prev,
      aiExtraction: {
        extractionId: data.extractionId,
        businessDate: data.businessDate,
        dataTypes: data.dataTypes,
        preferredModel: data.preferredModel || 'haiku',
        userPrompt: data.userPrompt || null,
        revenueTypeOverrides: data.revenueTypeOverrides || null,
        documentInfo: data.documentInfo,
        confirmed: data.confirmed || false,
      },
    }));
  }, []);

  const handleSaveSchema = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const targetId = locationId || 'default';

      // AI path: persist the extraction settings so the automated run repeats
      // what we just did by hand. The import itself stays a deliberate action
      // on the Upload tab.
      if (aiConfirmed && aiExtractedData?.extractedData) {
        const overrides = aiExtractedData.revenueTypeOverrides || schemaConfig.aiExtraction?.revenueTypeOverrides || null;
        const userPrompt = schemaConfig.aiExtraction?.userPrompt || aiExtractedData?.userPrompt || null;
        await saveSchemaConfig(targetId, schemaConfig.fileType, {
          ...schemaConfig,
          etlFunction: 'aiExtraction',
          etlTableConfig: {
            dataTypes: schemaConfig.aiExtraction?.dataTypes || aiExtractedData?.dataTypes || ['revenue', 'roomStats'],
            preferredModel: schemaConfig.aiExtraction?.preferredModel || aiExtractedData?.preferredModel || 'haiku',
            ...(userPrompt && { userPrompt }),
            ...(overrides && { revenueTypeOverrides: overrides }),
          },
          emailIntegration: emailConfig,
          aiExtractionInfo: {
            extractionId: aiExtractedData.extractionId,
            businessDate: aiExtractedData.businessDate,
            dataTypes: aiExtractedData.dataTypes,
            preferredModel: aiExtractedData.preferredModel,
            userPrompt: aiExtractedData.userPrompt,
            documentInfo: aiExtractedData.documentInfo,
          },
          updatedAt: new Date().toISOString(),
        });
        onExit();
        return;
      }

      // Which normalizer the pipeline should hand this feed to.
      let etlFunction;
      let etlTableConfig = schemaConfig.etlTableConfig || {};
      if (schemaConfig.ocrService === 'aiExtraction' || schemaConfig.extractionMethod === 'aiExtraction') {
        etlFunction = 'aiExtraction';
        const userPrompt = schemaConfig.aiExtraction?.userPrompt || aiExtractedData?.userPrompt || null;
        const overrides = schemaConfig.aiExtraction?.revenueTypeOverrides || aiExtractedData?.revenueTypeOverrides || null;
        etlTableConfig = {
          ...etlTableConfig,
          dataTypes: schemaConfig.aiExtraction?.dataTypes || aiExtractedData?.dataTypes || ['revenue', 'roomStats'],
          preferredModel: schemaConfig.aiExtraction?.preferredModel || aiExtractedData?.preferredModel || 'haiku',
          ...(userPrompt && { userPrompt }),
          ...(overrides && { revenueTypeOverrides: overrides }),
        };
      } else if (schemaConfig.fileType === 'historical') {
        etlFunction = 'historicalRevenue';
      } else if (schemaConfig.fileType === 'market' && schemaConfig.fieldMappings?.marketRevenue?.columnMappings?.length > 0) {
        // Only route to the hotelMarketRevenue normalizer when the marketRevenue
        // category was actually mapped. A 'market' file whose segments are mapped
        // into the Revenue category belongs in hotelRevenue and must fall through
        // to schemaDriven — hard-routing every 'market' fileType here sent those
        // configs to a table they never populate, and they imported 0 rows.
        etlFunction = 'schemaDrivenMarket';
      } else {
        etlFunction = schemaConfig.fieldMappings?.roomStats?.useNormalized ? 'normalizedSchemaDriven' : 'schemaDriven';
      }

      await saveSchemaConfig(targetId, schemaConfig.fileType, {
        ...schemaConfig,
        etlFunction,
        etlTableConfig,
        emailIntegration: emailConfig,
        aiExtractedData: aiExtractedData?.extractedData || null,
        aiExtractionInfo: aiExtractedData ? {
          extractionId: aiExtractedData.extractionId,
          businessDate: aiExtractedData.businessDate,
          dataTypes: aiExtractedData.dataTypes,
          preferredModel: aiExtractedData.preferredModel,
          userPrompt: aiExtractedData.userPrompt,
          documentInfo: aiExtractedData.documentInfo,
        } : null,
        updatedAt: new Date().toISOString(),
      });
      onExit();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, aiConfirmed, aiExtractedData, schemaConfig, emailConfig, onExit]);

  /**
   * Editing an existing feed's matching rules shouldn't mean re-uploading a
   * sample and walking through extraction just to reach Save. When the
   * Recognise step is showing a saved feed, this writes only its matching
   * rules and leaves mappings / extraction settings as they are.
   */
  const emailFileType = emailConfig.fileType === 'custom' ? emailConfig.customFileType : emailConfig.fileType;
  const canSaveMatchRules = !!existingSchema?.fileType && seededRef.current
    && String(emailFileType || '').toLowerCase() === String(existingSchema.fileType).toLowerCase();

  const handleSaveMatchRules = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // The full saved document rides along so a backend without the
      // matchRulesOnly path still re-saves the feed intact.
      const response = await saveSchemaConfig(locationId, existingSchema.fileType, {
        ...existingSchema,
        emailIntegration: { ...emailConfig, fileType: existingSchema.fileType },
        matchRulesOnly: true,
      });
      if (response && response.success === false) throw new Error(response.error || 'Failed to save');
      onExit();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, existingSchema, emailConfig, onExit]);

  const saveIntegration = useCallback(async (fileType, buildPayload) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!locationId) throw new Error('No location selected');
      await saveSchemaConfig(locationId, fileType, buildPayload());
      onExit();
    } catch (err) {
      console.error(`Error saving ${fileType} config:`, err);
      setError(err.message || `Failed to save the ${fileType} configuration`);
    } finally {
      setIsLoading(false);
    }
  }, [locationId, onExit]);

  const orgIdForLocation = selectedLocation?.orgId ? Number(selectedLocation.orgId) : null;

  // ── Step bodies ──────────────────────────────────────────────
  const renderStep = (key) => {
    switch (key) {
      case 'source':
        return (
          <WizardFrame
            title="What are we connecting?"
            step={stepNumber('source')}
            ofSteps={steps.length}
            subtitle="Pick the property this feed belongs to and how its data reaches us."
            next={{
              label: isIntegration ? 'Configure connection' : 'Continue',
              onClick: () => goto(isIntegration ? 'connect' : 'match'),
              disabled: !locationId,
            }}
            note={!locationId ? 'Choose a location to continue.' : null}
            noteTone="warn"
          >
            <EtlSection title="Property">
              <EtlGrid>
                <EtlField label="Organization" help="Follows the scope you picked on the previous screen.">
                  <EtlReadonly>{orgLabel}</EtlReadonly>
                </EtlField>
                <EtlField label="Location" required help="Only locations you have access to are listed.">
                  {locationsLoading ? <EtlReadonly>Loading locations…</EtlReadonly> : (
                    <FilterSelect
                      width="100%"
                      value={locationId}
                      options={locationOptions}
                      onChange={(v) => {
                        // A different property is a different feed — allow the
                        // seed effect to run again for its saved document.
                        seededRef.current = false;
                        setLocationId(v);
                        setSchemaConfig((prev) => ({ ...prev, hotelId: v }));
                      }}
                      placeholder="Choose a location…"
                      ariaLabel="Location"
                    />
                  )}
                </EtlField>
              </EtlGrid>
            </EtlSection>

            <EtlSection title="How the data arrives">
              <ChoiceCards
                ariaLabel="Source kind"
                options={SOURCE_KINDS}
                value={sourceKind}
                onChange={(v) => {
                  if (v === sourceKind) return;
                  // A different kind of source means a different set of steps,
                  // so anything reached beyond this point no longer applies.
                  setSourceKind(v);
                  setMaxReachedKey('source');
                  setVisited(new Set(['source']));
                }}
              />
            </EtlSection>

            {!isIntegration && locationId ? (
              <EtlSection title="Existing configuration">
                {checkingSchema ? (
                  <div className="cp-help">Checking this location for a saved schema…</div>
                ) : existingSchema ? (
                  <EtlBanner
                    tone="info"
                    title={`A schema already exists for "${existingSchema.fileType}"`}
                    actions={(
                      <ToolbarButton small icon={Code} onClick={() => setShowJsonEditor(true)}>Edit raw JSON</ToolbarButton>
                    )}
                  >
                    It is loaded and ready to edit — the steps ahead are filled in from it.
                    {existingSchema.updatedAt ? ` Last saved ${new Date(existingSchema.updatedAt).toLocaleDateString()}.` : ''}
                  </EtlBanner>
                ) : (
                  <div className="cp-help">No saved schema found for this location — you are creating the first one.</div>
                )}
              </EtlSection>
            ) : null}
          </WizardFrame>
        );

      case 'match':
        return (
          <WizardFrame
            title="How do we recognise this file?"
            step={stepNumber('match')}
            ofSteps={steps.length}
            subtitle="The pipeline matches every inbound file against these rules to decide which feed it belongs to."
            back={{ onClick: () => goto('source') }}
            next={{
              label: 'Continue to sample file',
              onClick: () => goto('upload'),
              disabled: !emailConfig.auditFile?.trim() && !(Array.isArray(emailConfig.keywords) && emailConfig.keywords.length > 0),
            }}
            secondary={canSaveMatchRules ? (
              <ToolbarButton
                onClick={handleSaveMatchRules}
                disabled={isLoading || (!emailConfig.auditFile?.trim() && !(emailConfig.keywords || []).length)}
                title="Save these matching rules now — mappings and extraction settings stay as they are"
              >
                {isLoading ? 'Saving…' : 'Save changes'}
              </ToolbarButton>
            ) : null}
            note={!emailConfig.auditFile?.trim() && !(emailConfig.keywords || []).length
              ? 'Add a file name or at least one match keyword.' : null}
            noteTone="warn"
          >
            {error && stepKey === 'match' ? <EtlBanner tone="error" title="Could not save">{error}</EtlBanner> : null}
            <EmailIntegrationConfig onEmailConfigChange={handleEmailConfigChange} initialConfig={emailConfig} />
          </WizardFrame>
        );

      case 'upload':
        return (
          <WizardFrame
            title="Upload a sample file"
            step={stepNumber('upload')}
            ofSteps={steps.length}
            subtitle="We read one real file to learn its shape. Nothing is imported at this step."
            back={{ onClick: () => goto('match') }}
            next={stepFooter?.next}
            secondary={stepFooter?.secondary}
            note={stepFooter?.note}
          >
            {error ? <EtlBanner tone="error" title="Could not process the file">{error}</EtlBanner> : null}
            <FileUploadComponent
              onFileUpload={handleFileUpload}
              isLoading={isLoading}
              error={null}
              hideFooter
              onFooterChange={footerFor('upload')}
              fileType={emailConfig.fileType === 'custom' ? emailConfig.customFileType : emailConfig.fileType}
            />
          </WizardFrame>
        );

      case 'preview':
        return (
          <WizardFrame
            title="What we read"
            step={stepNumber('preview')}
            ofSteps={steps.length}
            subtitle={readMethod === 'textract' ? 'AWS Textract extracted these tables and text. Continue to manual mapping.' : readMethod === 'formRecognizer' ? 'Azure Form Recognizer extracted these tables and text. Continue to manual mapping.' : 'Check the file contents before manual mapping.'}
            flush
            back={{ onClick: () => goto('upload') }}
            next={{ label: 'Continue', onClick: () => goto(stepAfter('preview')) }}
          >
            <DataPreviewTabs extractedData={extractedData} hideFooter />
          </WizardFrame>
        );

      case 'extract':
        return (
          <WizardFrame
            title="Extraction"
            step={stepNumber('extract')}
            ofSteps={steps.length}
            subtitle="Let the model read the document and propose rows. Confirmed output skips manual field mapping."
            flush
            back={{ onClick: () => goto(extractedData ? 'preview' : 'upload') }}
            next={stepFooter?.next}
            secondary={stepFooter?.secondary}
            note={stepFooter?.note}
          >
            {error ? <EtlBanner tone="error" title="Could not read the file">{error}</EtlBanner> : null}
            <AiExtractionTab
              uploadedFile={uploadedFile}
              extractedData={extractedData}
              schemaConfig={schemaConfig}
              onMappingChange={handleMappingChange}
              onExtractedData={handleAiExtractedData}
              hideFooter
              onFooterChange={footerFor('extract')}
              onNext={() => goto(aiConfirmedRef.current ? 'save' : 'map')}
              onSkip={skipToMapping}
              skipLabel={uploadedFile?.name?.toLowerCase().endsWith('.pdf') ? 'Choose OCR instead' : 'Skip AI and map manually'}
              isSkipping={isLoading}
              selectedLocation={selectedLocation}
            />
          </WizardFrame>
        );

      case 'historical':
        return (
          <WizardFrame
            title="Historical mapping"
            step={stepNumber('historical')}
            ofSteps={steps.length}
            subtitle="Map each column of the historical export to a revenue type or room statistic."
            flush
            back={{ onClick: () => goto(extractedData ? 'preview' : 'upload') }}
            next={{ label: 'Continue to review', onClick: () => goto('save') }}
          >
            <HistoricalRevenueMapping
              extractedData={extractedData}
              schemaConfig={schemaConfig}
              onMappingChange={handleMappingChange}
              hideFooter
              selectedLocation={selectedLocation}
            />
          </WizardFrame>
        );

      case 'map':
        return (
          <WizardFrame
            title="Field mapping"
            step={stepNumber('map')}
            ofSteps={steps.length}
            subtitle="Point each column at the table and field it belongs in."
            flush
            back={{ onClick: () => goto(stepBefore('map')) }}
            next={{ label: 'Continue to review', onClick: () => goto('save') }}
          >
            <FieldMappingPanel
              extractedData={extractedData}
              schemaConfig={schemaConfig}
              fileType={emailConfig.fileType}
              onMappingChange={handleMappingChange}
              hideFooter
            />
          </WizardFrame>
        );

      case 'save':
        return (
          <WizardFrame
            title="Review & save"
            step={stepNumber('save')}
            ofSteps={steps.length}
            subtitle="This is what the pipeline will use for every future file that matches this feed."
            flush
            back={{ onClick: () => goto(stepBefore('save')) }}
            next={{
              label: 'Save configuration',
              onClick: handleSaveSchema,
              busy: isLoading,
              busyLabel: 'Saving…',
            }}
            secondary={stepFooter?.secondary}
          >
            {error ? <div style={{ padding: '14px 18px 0' }}><EtlBanner tone="error" title="Could not save">{error}</EtlBanner></div> : null}
            <SchemaPreview
              schemaConfig={schemaConfig}
              extractedData={extractedData}
              aiExtractedData={aiExtractedData}
              emailConfig={emailConfig}
              hideFooter
              onFooterChange={footerFor('save')}
            />
          </WizardFrame>
        );

      case 'connect': {
        const common = {
          selectedLocation,
          onBack: () => goto('source'),
          isLoading,
          error,
          initialConfig: integrationInitialConfig,
        };
        if (sourceKind === 'toast_sftp_integration') {
          return (
            <WizardFrame title="Toast SFTP connection" flush>
              <ToastSftpConfig
                {...common}
                onSave={(sftpConfig) => saveIntegration('toast_sftp_integration', () => ({
                  hotelId: locationId,
                  fileType: 'toast_sftp_integration',
                  integrationType: 'sftp',
                  sftpConfig,
                  updatedAt: new Date().toISOString(),
                }))}
              />
            </WizardFrame>
          );
        }
        if (sourceKind === 'mews') {
          return (
            <WizardFrame title="Mews PMS connection" flush>
              <MewsConfig
                {...common}
                onSave={(mewsConfig) => saveIntegration('mews', () => ({
                  hotelId: locationId,
                  fileType: 'mews',
                  auditFile: 'mews',
                  integrationType: 'pms',
                  etlFunction: 'mews',
                  mewsConfig,
                  updatedAt: new Date().toISOString(),
                }))}
              />
            </WizardFrame>
          );
        }
        if (sourceKind === 'm3') {
          return (
            <WizardFrame title="M3 Accounting connection" flush>
              <M3Config
                {...common}
                onSave={(m3Config) => saveIntegration('m3', () => ({
                  hotelId: locationId,
                  fileType: 'm3',
                  auditFile: 'm3',
                  integrationType: 'accounting',
                  etlFunction: 'm3',
                  m3Config,
                  updatedAt: new Date().toISOString(),
                }))}
              />
            </WizardFrame>
          );
        }
        return (
          <WizardFrame title="Oracle OHIP connection" flush>
            <OhipConfig
              {...common}
              onSave={(ohipConfig) => saveIntegration('ohip', () => ({
                hotelId: locationId,
                ...(orgIdForLocation ? { orgId: orgIdForLocation } : {}),
                fileType: 'ohip',
                auditFile: 'ohip',
                integrationType: 'pms',
                etlFunction: 'ohip',
                ohipConfig: { ...ohipConfig, ...(orgIdForLocation ? { orgId: orgIdForLocation } : {}) },
                updatedAt: new Date().toISOString(),
              }))}
            />
          </WizardFrame>
        );
      }

      default:
        return null;
    }
  };

  if (locationsLoading && !locations.length) {
    return <div className="cp-page ep-etl"><EtlLoading>Loading locations…</EtlLoading></div>;
  }

  return (
    <div className="cp-page ep-etl">
      <div className="ep-takeover-bar">
        <div className="ep-crumb">
          <button type="button" className="cp-btn cp-ghost cp-sm" onClick={onExit}>ETL Configuration</button>
          <span className="ep-crumb-sep">/</span>
          <strong>
            {selectedLocation ? `${selectedLocation.name} — ` : ''}
            {isIntegration ? (SOURCE_KINDS.find((k) => k.value === sourceKind)?.title || 'Integration') : (schemaConfig.fileType || 'New feed')}
          </strong>
        </div>
        <div className="cp-toolbar-spacer" />
        {locationId && !isIntegration ? (
          <ToolbarButton icon={Package} onClick={() => setShowZipBundles(true)} title="Approve zip attachments for this property">
            Zip bundles
          </ToolbarButton>
        ) : null}
        {existingSchema && !isIntegration ? (
          <ToolbarButton icon={Code} onClick={() => setShowJsonEditor(true)}>Raw JSON</ToolbarButton>
        ) : null}
        <ToolbarButton icon={X} onClick={onExit}>Close</ToolbarButton>
      </div>

      <div className={`ep-wizard${steps.length < 2 ? ' ep-no-rail' : ''}`}>
        <StepRail steps={railSteps} current={stepKey} onSelect={goto} />
        {/* Visited steps stay mounted and are hidden, not unmounted — see the
            note on `visited` above. React keeps each one's internal state, so
            Back is genuinely non-destructive. */}
        <div style={{ minWidth: 0 }}>
          {steps
            .filter((s) => visited.has(s.key))
            .map((s) => (
              <div
                key={DOWNSTREAM.includes(s.key) ? `${s.key}-${dataEpoch}` : s.key}
                hidden={s.key !== stepKey}
                style={s.key === stepKey ? undefined : { display: 'none' }}
              >
                {renderStep(s.key)}
              </div>
            ))}
        </div>
      </div>

      {showJsonEditor && existingSchema ? (
        <SchemaJsonEditor
          initialConfig={existingSchema}
          hotelId={locationId}
          onClose={() => setShowJsonEditor(false)}
          onSave={(updated) => setExistingSchema(updated)}
        />
      ) : null}

      {showZipBundles && locationId ? (
        <div className="cp-modal-scrim" role="dialog" aria-label="Zip bundle approvals">
          <div className="cp-modal cp-wide">
            <ZipBundleConfig hotelId={locationId} onClose={() => setShowZipBundles(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
